import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';
import { useEffect } from 'react';

import {
  IcChangeOrderIcon,
  IcCloseIcon,
  IcDeleteIcon,
  IcMarkAsReadIcon,
  IcMarkAsUnreadIcon,
  IcMuteIcon,
  IcPinStrokeIcon,
  IcUnmuteIcon,
} from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';
import { IPopover, IPopoverMenuItem, MenuItemStyle } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { AvatarTypes } from 'APP/components/Avatar/Avatar';
import { MAX_PIN_GROUPS } from 'APP/constants/group';
import { IUserNameBadgeIcon, mapUserProfileBadgeToIcon } from 'APP/constants/userBadges';
import { useMoveGroupToSpace } from 'APP/main/hooks/space/useMoveGroupToSpace';
import { GroupType } from 'APP/model/group/groupModel.types';
import { Features } from 'APP/packages/features/Features';
import { useNavigateTo } from 'APP/router/hooks/useNavigateTo';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import { SecretGroup } from 'STORE/Groups/SecretGroup/SecretGroup';
import { Thread } from 'STORE/Groups/Thread';
import { Space } from 'STORE/Spaces/Space';

interface IGroupListItemPresenter {
  childPopup: PopupStore | null;
  isActive: boolean;
  space: Space | null;
  isShowSpaceIcon: boolean;
  actions: IPopoverMenuItem[];
  canPin: boolean;
  isShowBotIcon: boolean;
  isShowSecureIcon: boolean;
  isShowMutedIcon: boolean;
  userNameBadge: IUserNameBadgeIcon | null;
  verified: boolean;
  avatarType: AvatarTypes;
  hasAvatarLockIcon: boolean;
  canDeleteGroup: boolean;
  lastMessageTs: number;
  deleteGroup(): Promise<void>;
  onClick(): void;
}
export const useGroupListItemPresenter = (
  group: Group,
  isShowSpaceIcon?: boolean,
  popover?: IPopover<HTMLDivElement>
): IGroupListItemPresenter => {
  const { t } = useTranslation();
  const navigateTo = useNavigateTo();
  const source = useAsObservableSource({
    group,
    navigateTo,
  });

  const moveGroupToSpace = useMoveGroupToSpace(group.id);

  const presenter = useLocalStore<IGroupListItemPresenter>(() => ({
    childPopup: null,

    onClick(): void {
      if (source.group instanceof SecretGroup && !source.group.isUnlocked) {
        return;
      }
      popover?.close();
      source.navigateTo({
        to: `/${source.group.route}/${source.group.id}`,
      });
      if (Entities.GroupsStore.activeGroupId === source.group.id) {
        Tasks.messaging.scrollToLastMessage();
      }
    },

    get isActive(): boolean {
      if (Entities.GroupsStore.activeGroup) {
        const activeGroup = Entities.GroupsStore.activeGroup;

        if (activeGroup instanceof Thread && activeGroup.isThread && activeGroup.threadInfo) {
          return source.group.id === activeGroup.threadInfo.groupId;
        }

        const groupId = activeGroup instanceof Schedule ? activeGroup.groupId : null;

        return (
          source.group.id === Entities.GroupsStore.activeGroup.id || source.group.id === groupId
        );
      }
      return false;
    },

    get space(): Space | null {
      return source.group?.spaceId ? Entities.spacesStore.getById(source.group.spaceId) : null;
    },

    get isShowSpaceIcon(): boolean {
      return Features.isSpacesEnabled() && !!isShowSpaceIcon && !!presenter.space;
    },

    get actions(): IPopoverMenuItem[] {
      const actions = [];

      if (source.group.unread || source.group.numberOfUnread > 0) {
        actions.push({
          key: 'read',
          title: t('action_over_chat_mark_as_read'),
          icon: IcMarkAsReadIcon,
          onClick() {
            if (Features.isMarkGroupAsReadUnreadEnabled() && source.group.unread) {
              Tasks.group.markGroupsAsUnread([source.group.id], false);
            }
            if (source.group.lastMessage?.unread) {
              Tasks.messaging.confirmReadMessages({
                groupId: source.group.id,
                messageId: source.group.lastMessage.id,
              });
            }
          },
        });
      } else if (Features.isMarkGroupAsReadUnreadEnabled()) {
        actions.push({
          key: 'unread',
          title: t('action_over_chat_mark_as_unread'),
          icon: IcMarkAsUnreadIcon,
          onClick() {
            Tasks.group.markGroupsAsUnread([source.group.id], true);
          },
        });
      }

      if (presenter.canPin && !source.group.isPinned) {
        actions.push({
          key: 'pin',
          title: source.group.isBot
            ? t('bot_pin')
            : source.group.isChannel
            ? t('channel_pin')
            : t('group_chat_pin'),
          icon: IcPinStrokeIcon,
          onClick() {
            Tasks.group.pinGroup({ groupId: source.group.id });
          },
        });
      }

      if (source.group.isPinned) {
        actions.push({
          key: 'unpin',
          title: source.group.isBot
            ? t('bot_unpin')
            : source.group.isChannel
            ? t('channel_unpin')
            : t('group_chat_unpin'),
          icon: IcPinStrokeIcon,
          onClick() {
            Tasks.group.unpinGroup({ groupId: source.group.id });
          },
        });
      }

      if (!source.group.muted && !source.group.isSavedMessages && !source.group.isServiceMessages) {
        actions.push({
          key: 'mute',
          title: t('action_over_chat_mute'),
          icon: IcMuteIcon,
          onClick() {
            Tasks.group.muteNotifications(source.group.id);
          },
        });
      }

      if (source.group.muted && !source.group.isSavedMessages && !source.group.isServiceMessages) {
        actions.push({
          key: 'unmute',
          title: t('action_over_chat_unmute'),
          icon: IcUnmuteIcon,
          onClick() {
            Tasks.group.unMuteNotifications(source.group.id);
          },
        });
      }

      if (moveGroupToSpace.canMoveToSpace) {
        actions.push({
          key: 'move-to-space',
          title: t('space_moveto_space_button'),
          icon: IcChangeOrderIcon,
          onClick: () => (presenter.childPopup = moveGroupToSpace.moveToSpace()),
        });
      }

      if (moveGroupToSpace.canRemoveFromSpace) {
        actions.push({
          key: 'remove-from-space',
          title: t('space_remove_from_button', { 0: moveGroupToSpace.space?.name }),
          icon: IcCloseIcon,
          onClick: () => moveGroupToSpace.removeFromSpace(),
        });
      }

      if (presenter.canDeleteGroup) {
        let title;

        if (source.group.isSavedMessages || source.group.isP2P) {
          title = t('chat_details_delete_chat');
        } else if (source.group.isChannel) {
          title = t('channel_delete');
        } else {
          title = t('delete_group');
        }

        actions.push({
          key: 'delete_group',
          title,
          icon: IcDeleteIcon,
          style: MenuItemStyle.Danger,
          onClick: presenter.deleteGroup,
        });
      }

      return actions;
    },

    get canPin(): boolean {
      const pinnedCount = source.group.isChannel
        ? Entities.GroupsStore.pinnedChannels.length
        : Entities.GroupsStore.pinnedChatGroups.length;

      return pinnedCount < MAX_PIN_GROUPS;
    },

    get isShowBotIcon(): boolean {
      return source.group.isBot;
    },

    get isShowSecureIcon(): boolean {
      return source.group.type === GroupType.SecretGroup;
    },

    get isShowMutedIcon(): boolean {
      return source.group.muted && !source.group.isSavedMessages;
    },

    get userNameBadge(): IUserNameBadgeIcon | null {
      const isP2p = source.group.isP2P;

      if (!isP2p) {
        return null;
      }

      const opponentId = source.group.opponentId;

      const user = opponentId ? Entities.usersStore.getUserById(opponentId) : null;

      if (!user || !user.usernameBadge) {
        return null;
      }

      return mapUserProfileBadgeToIcon[user.usernameBadge] || null;
    },

    get verified(): boolean {
      const isP2p = source.group.isP2P;

      if (!isP2p) {
        return source.group.verified;
      }

      const opponentId = source.group.opponentId;

      const user = opponentId ? Entities.usersStore.getUserById(opponentId) : null;

      return user?.verified || false;
    },

    get avatarType(): AvatarTypes {
      return source.group.isChannel ? AvatarTypes.Square_20 : AvatarTypes.Round_10;
    },

    get hasAvatarLockIcon(): boolean {
      return source.group.isChannel && source.group.isClosed;
    },

    get canDeleteGroup(): boolean {
      return (
        ((source.group.isChatGroup || source.group.isChannel) && source.group.isOwner) ||
        source.group.isSavedMessages ||
        (source.group.isP2P && !source.group.isBot)
      );
    },

    get lastMessageTs(): number {
      return source.group.lastMessage?.displayTime || 0;
    },

    async deleteGroup(): Promise<void> {
      let text;

      if (source.group.isP2P) {
        text = t('delete_chat_alert', { 0: source.group.avatarTitle });
      } else if (source.group.isSavedMessages) {
        text = t('saved_messages_delete_chat_alert', { 0: source.group.name });
      } else if (source.group.isChannel) {
        text = t('delete_channel_alert');
      } else {
        text = t('delete_group_chat_alert');
      }

      const canDelete = await Tasks.app.showConfirm({
        text,
        primaryText: t('common_ok'),
        secondaryText: t('common_cancel'),
      });

      if (!canDelete) {
        return;
      }

      let error;
      if (source.group.isP2P) {
        error = (
          await Tasks.group.leaveGroup({
            groupId: source.group.id,
          })
        ).error;
      } else {
        error = (
          await Tasks.group.deleteGroup({
            groupId: source.group.id,
          })
        ).error;
      }

      if (error) {
        Entities.toast.show(t('something_went_wrong'));
      }
    },
  }));

  useEffect(() => {
    return () => presenter.childPopup?.close();
  }, []);

  return presenter;
};
